/* ==========================================================================
   Search bar + Searchanise autocomplete overlay -- behaviour
   Task: https://app.clickup.com/t/86d3tnu9v  "Redesign Search Bar and Overlay"

   Pairs with assets/search-overlay.css. Everything here covers a mockup
   requirement Searchanise has no setting for:

     1. mobile leading icon toggles between magnifier and "back"
     2. mobile trailing icon toggles between microphone and "clear"
     3. voice search (Web Speech API), hidden when unsupported, with the app's
        own microphone suppressed so the two cannot both appear
     4. "view all results" CTA moved under the product grid
     5. on mobile, products (and their CTA) lifted to the top of the panel
     6. body.snize-search-idle so the dropdown can collapse before you type
     7. on mobile, the panel measured to the full width of the search bar
     8. on mobile, Popular suggestions capped at 3 once a query exists

   Two more cover app behaviour that is missing or broken outright
   (ClickUp 86d3y86uf):

     9. a Recent searches section, built from the history Searchanise already
        records but does not render
    10. a fallback on the results page when the autocomplete links to a tab
        that page does not have

   And one from the follow-up (ClickUp 86d42kt65):

    11. a hover box revealing the full product name wherever the two-line
        title clamp cut it

   And one for an A/B test (ClickUp 86d40tt5j):

    12. the product list capped at the "Products shown in the search
        dropdown" theme setting, so a variant theme can show 3 while the app
        sends 6

   No dependencies -- Searchanise loads deferred and jQuery is not guaranteed
   to be ready when the header renders.
   ========================================================================== */

(function () {
    'use strict';

    var MOBILE_QUERY = '(max-width: 767px)';
    var RESULTS_ID = 'snize-instant-search-results';

    /* Read while this deferred script is still executing -- currentScript is
       null inside any later callback. 0 means "show what the app sends". */
    var PRODUCT_LIMIT = (function () {
        var script = document.currentScript;
        var value = script ? parseInt(script.dataset.productLimit, 10) : 0;
        return value > 0 ? value : 0;
    })();

    /* Frames to wait for search-overlay.css before giving up on it. ~5s at
       60fps, long enough for a cold cache on a slow connection. */
    var STYLE_WAIT_FRAMES = 300;

    /* ----------------------------------------------------------------------
       Stylesheet gate

       search-overlay.css is loaded non-render-blocking (media="print", flipped
       to "all" on load -- see layout/theme.liquid), so it can land *after* this
       deferred script has already run. Anything revealed before then paints
       with UA button styling, and on desktop paints at all: the rule that hides
       the lead / voice / clear buttons above 768px lives in that sheet, so the
       first load showed a "back" chevron and a clear "x" wedged into the
       desktop search bar until the CSS caught up.

       The sheet announces itself with --search-overlay-ready, so this waits on
       a property rather than on a <link> -- nothing here depends on the file
       name or on how the tag was injected. If the sheet never arrives the
       reveal simply never happens and the bar keeps its plain submit button,
       which is the same fallback as the script not running at all.
       ---------------------------------------------------------------------- */

    function whenStyled(root, run) {
        var frames = 0;

        function ready() {
            return (
                window
                    .getComputedStyle(root)
                    .getPropertyValue('--search-overlay-ready')
                    .trim() === '1'
            );
        }

        (function poll() {
            if (ready()) {
                run();
                return;
            }
            if (++frames > STYLE_WAIT_FRAMES) return;
            window.requestAnimationFrame(poll);
        })();
    }

    /* ----------------------------------------------------------------------
       Search bar
       ---------------------------------------------------------------------- */

    function initSearchBar(root) {
        var input = root.querySelector('.site-header__search-input');
        if (!input || root.dataset.searchOverlayReady === 'true') return;
        root.dataset.searchOverlayReady = 'true';

        var lead = root.querySelector('[data-search-lead]');
        var voice = root.querySelector('[data-search-voice]');
        var clear = root.querySelector('[data-search-clear]');

        function syncQueryState() {
            root.classList.toggle('has-query', input.value.trim() !== '');
            syncIdleClass();
        }

        input.addEventListener('input', syncQueryState);
        input.addEventListener('change', syncQueryState);
        syncQueryState();

        function setOpen(open) {
            root.classList.toggle('is-open', open);
            if (lead) {
                lead.setAttribute(
                    'aria-label',
                    (open
                        ? lead.dataset.labelBack
                        : lead.dataset.labelSearch) ||
                        lead.getAttribute('aria-label'),
                );
            }
        }

        input.addEventListener('focus', function () {
            setOpen(true);
            syncOverlayGeometry(root);
            /* Searchanise builds its widget -- and its own microphone -- on the
               first focus, so this is the earliest useful moment to catch it. */
            suppressAppVoiceTrigger();
        });

        /* The overlay outlives blur (you can scroll it), so the authority on
           "still open" is Searchanise's own body class. */
        input.addEventListener('blur', function () {
            window.setTimeout(function () {
                if (
                    !document.body.classList.contains(
                        'snize-instant-widget-is-open',
                    )
                ) {
                    setOpen(false);
                }
            }, 150);
        });

        if (lead) {
            lead.addEventListener('click', function () {
                if (root.classList.contains('is-open')) {
                    closeOverlay(input);
                } else {
                    input.focus();
                }
            });
        }

        if (clear) {
            clear.addEventListener('click', function () {
                input.value = '';
                notifyTyping(input);
                syncQueryState();
                input.focus();
            });
        }

        var voiceSupported = voice
            ? initVoiceSearch(voice, input, syncQueryState)
            : false;

        /* Reveal the mobile controls and let the stylesheet retire the submit
           button. Both are deliberately gated on this script running -- see the
           markup comment in blocks/header-search.liquid -- and on the
           stylesheet having landed, so the buttons never paint unstyled. */
        whenStyled(root, function () {
            root.classList.add('js-search-enhanced');
            if (lead) lead.hidden = false;
            if (clear) clear.hidden = false;
            if (voiceSupported) voice.hidden = false;
        });
    }

    /* Dismiss the dropdown the way Searchanise expects: Escape, then blur.
       Escape is what the widget listens for -- it hides the panel and drops
       its own body class, so nothing here touches its inline styles. */
    function closeOverlay(input) {
        input.dispatchEvent(
            new KeyboardEvent('keydown', {
                key: 'Escape',
                keyCode: 27,
                which: 27,
                bubbles: true,
            }),
        );
        input.blur();
        hideTooltip();
        document
            .querySelectorAll('.header-search.is-open')
            .forEach(function (el) {
                el.classList.remove('is-open');
                var lead = el.querySelector('[data-search-lead]');
                if (lead && lead.dataset.labelSearch) {
                    lead.setAttribute('aria-label', lead.dataset.labelSearch);
                }
            });
    }

    /* Searchanise listens for real key events, so a programmatic value change
       (voice result, clear button) has to be announced. */
    function notifyTyping(input) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(
            new KeyboardEvent('keyup', { key: 'Unidentified', bubbles: true }),
        );
    }

    /* ----------------------------------------------------------------------
       Voice search

       Searchanise ships its own voice trigger (.snize-voice-search-trigger),
       enabled in the app for mobile only. It is injected into the bar a beat
       after the widget boots, sits absolutely at `right: 30px`, and never gives
       way to a clear button -- which is exactly the "microphone suddenly
       changes after a second" / "remains visible while typing" pair from the
       review. It only appears on a real handset, hence the mismatch with
       Inspector Mode. The mockup's microphone is this one, so the app's copy is
       hidden (see search-overlay.css) rather than deferred to.
       ---------------------------------------------------------------------- */

    /* Late-arriving copies have to be caught as well as the ones present at
       init -- the widget is injected on first interaction. CSS does the hiding;
       this is only for the case where it lands outside .header-search. */
    function suppressAppVoiceTrigger() {
        document
            .querySelectorAll('.header-search .snize-voice-search-trigger')
            .forEach(function (el) {
                el.hidden = true;
            });
    }

    /* Returns whether the browser can actually do this: the button stays
       [hidden] when it cannot, and initSearchBar reveals it otherwise once the
       stylesheet is in place. */
    function initVoiceSearch(button, input, syncQueryState) {
        var SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return false; // button stays [hidden]

        var recognition = null;
        var recording = false;

        function stop() {
            recording = false;
            button.classList.remove('is-recording');
            button.setAttribute('aria-pressed', 'false');
        }

        button.addEventListener('click', function () {
            if (recording && recognition) {
                recognition.stop();
                return;
            }

            try {
                recognition = new SpeechRecognition();
            } catch (e) {
                button.hidden = true;
                return;
            }

            recognition.lang = document.documentElement.lang || 'en-US';
            recognition.interimResults = false;
            recognition.maxAlternatives = 1;

            recognition.onstart = function () {
                recording = true;
                button.classList.add('is-recording');
                button.setAttribute('aria-pressed', 'true');
            };

            recognition.onresult = function (event) {
                var transcript =
                    event.results && event.results[0] && event.results[0][0];
                if (!transcript) return;
                input.value = transcript.transcript;
                input.focus();
                notifyTyping(input);
                syncQueryState();
            };

            recognition.onerror = stop;
            recognition.onend = stop;

            try {
                recognition.start();
            } catch (e) {
                stop();
            }
        });

        return true;
    }

    /* ----------------------------------------------------------------------
       Dropdown layout fixes

       Searchanise renders the "view all" CTA in the left column and the
       products in the right one. The mockup puts the CTA under the products,
       and on mobile puts the products at the very top of the panel.
       Both are DOM moves, re-applied after every autocomplete update because
       the widget rebuilds its lists in place.
       ---------------------------------------------------------------------- */

    function findList(results, itemSelector) {
        var lists = results.querySelectorAll('ul');
        for (var i = 0; i < lists.length; i++) {
            if (lists[i].querySelector(itemSelector)) return lists[i];
        }
        return null;
    }

    /* Align the panel with the whole search bar rather than just the <input>.
       Searchanise anchors the dropdown to the field, which on mobile begins after
       the leading magnifier and ends before the microphone, so both edges sit
       inside the bar. The measurements go out as custom properties and the
       stylesheet applies them with !important -- an author !important beats the
       widget's inline left/width without having to fight it on every update. */
    function syncOverlayGeometry(root) {
        var scope =
            root ||
            document.querySelector('.header-search.is-open') ||
            document.querySelector('.header-search');
        var form = scope && scope.querySelector('.site-header__search-form');
        if (!form) return;

        var rect = form.getBoundingClientRect();
        if (!rect.width) return; // hidden bar (e.g. the other header variant)

        /* Searchanise positions the panel against its offset parent, so the left
           edge has to be expressed in that same space. */
        var results = document.getElementById(RESULTS_ID);
        var parent = results && results.offsetParent;
        var left = rect.left;
        if (
            parent &&
            parent !== document.body &&
            parent !== document.documentElement
        ) {
            left += parent.scrollLeft - parent.getBoundingClientRect().left;
        } else {
            left += window.pageXOffset;
        }

        var style = document.documentElement.style;
        style.setProperty('--snize-ac-left', Math.round(left) + 'px');
        style.setProperty('--snize-ac-width', Math.round(rect.width) + 'px');
    }

    /* "Limit the Popular Suggestions section to 3 items on mobile ... keeping the
       product categories and search results higher on the screen." Only once
       something has been typed -- the idle panel has no products below the
       suggestions, so there is nothing to lift and all five stay useful.
       Recent searches are the same element with a .snize-recent modifier and are
       counted separately, so they are not trimmed. */
    var MOBILE_SUGGESTION_LIMIT = 3;

    function capSuggestions(results) {
        var trim =
            window.matchMedia(MOBILE_QUERY).matches &&
            !document.body.classList.contains('snize-search-idle');
        var shown = 0;

        results
            .querySelectorAll('.snize-suggestion:not(.snize-recent)')
            .forEach(function (item) {
                var hide = trim && shown >= MOBILE_SUGGESTION_LIMIT;
                item.classList.toggle('snize-suggestion-overflow', hide);
                if (!hide) shown++;
            });
    }

    /* "3 vs 6 products" A/B test (ClickUp 86d40tt5j). The app's product count
       is one store-wide setting with no A/B option, so it stays at the higher
       number and the variant theme hides the rest here. Hidden, not removed:
       the widget rebuilds this list on every keystroke and keeps its own
       references to the rows. The "View all" link is left alone, since its
       click rate is what the test measures. */
    function capProducts(results) {
        if (!PRODUCT_LIMIT) return;

        results
            .querySelectorAll('.snize-product')
            .forEach(function (item, index) {
                item.classList.toggle(
                    'snize-product-overflow',
                    index >= PRODUCT_LIMIT,
                );
            });
    }

    /* ----------------------------------------------------------------------
       Recent searches

       Searchanise keeps the shopper's own history in localStorage all by
       itself -- `snize-recent`, lowercased, oldest first, capped at 12 -- but
       it is not rendering the section in the dropdown, which is the open half
       of ClickUp 86d3y86uf. The data is already there, so the section is built
       from the widget's own store rather than waiting on the app, and it
       stands down the moment Searchanise starts rendering its own rows.

       Read-only on purpose. The app files `snize-recent` under its analytics
       consent category, so a shopper who declines analytics has no history
       stored and sees no section. Keeping our own copy would route around that.
       ---------------------------------------------------------------------- */

    var RECENT_KEY = 'snize-recent';
    var RECENT_SEPARATOR = ':|||:';
    var RECENT_LIMIT = 5; // same count as Popular suggestions

    function readRecentSearches() {
        var raw;
        try {
            raw = window.localStorage.getItem(RECENT_KEY);
        } catch (e) {
            return []; // storage unavailable (private mode, blocked cookies)
        }
        if (!raw) return [];

        /* The app stores the list JSON-encoded, so the raw item arrives wrapped
           in quotes -- without this the first and last terms render with a
           stray " attached. Tolerate a bare string in case that ever changes. */
        try {
            var decoded = JSON.parse(raw);
            if (typeof decoded === 'string') raw = decoded;
        } catch (e) {
            /* not JSON -- use the raw value */
        }

        return raw
            .split(RECENT_SEPARATOR)
            .filter(function (term) {
                return term.trim() !== '';
            })
            .reverse() // the app appends, so the newest is last
            .slice(0, recentLimit());
    }

    /* Same reasoning as the Popular suggestions cap: the idle panel on a phone
       now carries two lists, so the second one has to give way rather than push
       everything off the screen. */
    function recentLimit() {
        return window.matchMedia(MOBILE_QUERY).matches
            ? MOBILE_SUGGESTION_LIMIT
            : RECENT_LIMIT;
    }

    function recentLabel() {
        var host = document.querySelector('.header-search[data-recent-label]');
        return (host && host.dataset.recentLabel) || 'Recent searches';
    }

    /* Go straight to the results page rather than replaying the term through
       the widget: the form's action is the setting that decides where search
       lands (see blocks/header-search.liquid), so this follows it. */
    function submitSearch(term) {
        var input =
            document.querySelector(
                '.header-search.is-open .site-header__search-input',
            ) ||
            document.querySelector('.header-search .site-header__search-input');
        var form = input && input.form;
        var action =
            (form && form.getAttribute('action')) || '/pages/search-results-page';

        window.location.href =
            action +
            (action.indexOf('?') === -1 ? '?' : '&') +
            'q=' +
            encodeURIComponent(term);
    }

    function buildRecentList(terms) {
        var list = document.createElement('ul');
        list.className = 'snize-ac-results-list';
        list.dataset.searchRecent = terms.join(RECENT_SEPARATOR);

        var label = document.createElement('li');
        label.className = 'snize-label';
        label.textContent = recentLabel();
        list.appendChild(label);

        terms.forEach(function (term, index) {
            /* Same element the widget uses for a suggestion, so its own
               stylesheet supplies the row and the icon slot; .snize-recent is
               what search-overlay.css swaps the magnifier for a clock. */
            var item = document.createElement('li');
            item.className =
                'snize-suggestion snize-recent ' +
                (index % 2 ? 'snize-ac-even' : 'snize-ac-odd');
            item.setAttribute('role', 'button');
            item.setAttribute('tabindex', '0');
            item.setAttribute('aria-label', term);
            item.textContent = term;

            item.addEventListener('click', function () {
                submitSearch(term);
            });
            item.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    submitSearch(term);
                }
            });

            list.appendChild(item);
        });

        return list;
    }

    function renderRecentSearches(results) {
        var column = results.querySelector('.snize-ac-results-column');
        if (!column) return;

        var ours = results.querySelector('[data-search-recent]');
        var appRow = results.querySelector('.snize-suggestion.snize-recent');
        var appRendersRecent = !!appRow && (!ours || !ours.contains(appRow));

        /* History belongs to the empty state -- once there is a query the
           widget fills the panel with matches for it. */
        var terms =
            appRendersRecent ||
            !document.body.classList.contains('snize-search-idle')
                ? []
                : readRecentSearches();

        /* Bail when nothing would change. The MutationObserver driving this
           watches the same subtree, so an unconditional rebuild would retrigger
           itself forever. */
        var signature = terms.join(RECENT_SEPARATOR);
        if (ours && ours.parentElement === column) {
            if (ours.dataset.searchRecent === signature) return;
            column.removeChild(ours);
        } else if (ours) {
            ours.parentElement.removeChild(ours);
        }
        if (!terms.length) return;

        column.insertBefore(buildRecentList(terms), column.firstElementChild);
    }

    function layoutDropdown() {
        var results = document.getElementById(RESULTS_ID);
        if (!results) return;

        syncOverlayGeometry();
        capSuggestions(results);
        capProducts(results);
        /* Before the column moves below, so a mobile pass settles in one go. */
        renderRecentSearches(results);
        /* The widget rebuilds its product list on every keystroke, which drops
           the card the hover box was describing. This catches that. */
        positionTooltip();

        var columns = results.querySelectorAll('.snize-ac-results-column');
        if (columns.length < 2) return;

        var productList = findList(results, '.snize-product');
        var ctaList = results.querySelector('.snize-view-all-container');
        var isMobile = window.matchMedia(MOBILE_QUERY).matches;

        /* Remember where the widget originally put each list so desktop can be
           restored when the viewport grows back. */
        [productList, ctaList].forEach(function (list) {
            if (list && !list.dataset.snizeHome) {
                list.dataset.snizeHome =
                    list.parentElement === columns[0] ? 'first' : 'second';
            }
        });

        /* Every move is guarded by a position check. The MutationObserver that
           calls this function watches the same subtree, so an unconditional
           re-insert would retrigger it forever. */
        if (isMobile && productList) {
            /* Products first, then their CTA, then whatever the widget put in
               the left column (Popular suggestions, Inspirations). */
            if (columns[0].firstElementChild !== productList) {
                columns[0].insertBefore(
                    productList,
                    columns[0].firstElementChild,
                );
            }
            if (ctaList && productList.nextElementSibling !== ctaList) {
                productList.after(ctaList);
            }
        } else {
            if (
                productList &&
                productList.dataset.snizeHome === 'second' &&
                productList.parentElement !== columns[1]
            ) {
                columns[1].appendChild(productList);
            }
            // Desktop: CTA sits under the products regardless of where it began.
            if (
                ctaList &&
                productList &&
                productList.nextElementSibling !== ctaList
            ) {
                productList.after(ctaList);
            }
        }
    }

    /* ----------------------------------------------------------------------
       Idle state -- true only while every visible search input is empty.
       ---------------------------------------------------------------------- */

    function syncIdleClass() {
        var inputs = document.querySelectorAll(
            '.header-search .site-header__search-input',
        );
        var hasText = false;
        inputs.forEach(function (input) {
            if (input.value.trim() !== '') hasText = true;
        });
        document.body.classList.toggle('snize-search-idle', !hasText);
    }

    /* ----------------------------------------------------------------------
       Results page -- ?tab= values the page does not have

       Each section of the autocomplete links its "More" button at the results
       page with its own tab, e.g. ...?q=stamp&tab=categories. The results
       widget only draws the tabs the app is configured to show, and a value it
       does not know leaves every tab inactive and the panel empty -- "Nothing
       found for stamp" on a query with thousands of matches.

       That is what ClickUp 86d3y86uf reported, and the cause was the
       Collections tab being switched off in the app (Search results widget →
       Content → "Categories per page" was 0). Turning it on fixed it; this is
       the net underneath, for any tab value the page does not recognise.

       The test is against the tabs actually rendered rather than a list of
       names, so it stops firing for a tab the moment the app starts serving it,
       with no change here.
       ---------------------------------------------------------------------- */

    var TAB_SELECTOR = '.snize-main-panel-controls a';
    var TAB_SETTLE_MS = 500; // the widget marks the open tab a beat after drawing them
    var TAB_GIVE_UP_MS = 10000;

    /* The app labels the section "Collections" but names the tab "categories",
       so anything written against the label rather than the tab -- an old link,
       a bookmark, a hand-typed URL -- misses. Redirect those instead of
       dropping them. Only applied when the target tab actually rendered, so
       this cannot resurrect a tab the app has since switched off. */
    var TAB_ALIASES = { collections: 'categories' };

    function stripUnknownResultsTab() {
        var params = new URLSearchParams(window.location.search);
        if (!params.get('tab')) return;

        var settle = null;
        var observer = new MutationObserver(onMutate);
        var giveUp = window.setTimeout(stop, TAB_GIVE_UP_MS);

        function stop() {
            observer.disconnect();
            window.clearTimeout(settle);
            window.clearTimeout(giveUp);
        }

        function verdict() {
            stop();
            if (document.querySelector(TAB_SELECTOR + '.active')) return;

            /* Prefer sending them to the tab they meant; fall back to dropping
               it. Either way the next load has a tab the page knows, so this
               settles in one redirect and cannot bounce. */
            var alias = TAB_ALIASES[(params.get('tab') || '').toLowerCase()];
            if (alias && document.querySelector('.snize-main-panel-controls-' + alias)) {
                params.set('tab', alias);
            } else {
                params.delete('tab');
            }

            var query = params.toString();
            /* replace, not assign: the back button should return to wherever
               the shopper came from, not bounce off the broken URL. */
            window.location.replace(
                window.location.pathname +
                    (query ? '?' + query : '') +
                    window.location.hash,
            );
        }

        /* Judged once, on a timer started when the tabs first appear -- the
           widget keeps mutating as products render, and restarting the timer on
           every one of those would push the verdict out indefinitely. */
        function onMutate() {
            if (settle !== null || !document.querySelector(TAB_SELECTOR)) return;
            settle = window.setTimeout(verdict, TAB_SETTLE_MS);
        }

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });
        onMutate();
    }

    /* ----------------------------------------------------------------------
       Truncated product names -- hover to reveal the full title
       ClickUp 86d42kt65

       Section 6 of the stylesheet clamps a card title to two lines, so a long
       name ends in an ellipsis with no way to read the rest. The mockup puts
       the full name in a grey box under the title, on hover, and only where
       the clamp actually cut something.

       The box cannot be a pseudo-element on the card. .snize-title clips its
       own overflow -- that IS the clamp -- and both the <li> and the products
       <ul> carry overflow:hidden, so anything drawn on a card is cut at its
       edge, while the box is deliberately wider than the card. So it is one
       element instead, parked inside the dropdown (which sits at the maximum
       z-index, so nothing outside it could paint over the box) and placed with
       position:fixed, which ancestor overflow cannot clip. Nothing the app
       styles is touched.

       Two details keep it out of the widget's way:
         - the text goes in a data attribute and is drawn by content: attr(),
           not written as a text node, so showing the box never mutates the
           subtree watchDropdown() has under a MutationObserver
         - pointer-events: none, so it never takes a click meant for the card
       ---------------------------------------------------------------------- */

    var TOOLTIP_EDGE = 8; // keep the box this far inside the panel

    /* Read live rather than cached: a hybrid laptop can gain a mouse mid-session. */
    var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

    var tooltip = null;
    var tooltipTitle = null; // the .snize-title the box is currently describing
    var tooltipTracked = false; // scroll/resize listeners attached yet?

    /* The clamp is -webkit-line-clamp plus a max-height, so what overflows is
       the height. 1px of slack for sub-pixel line boxes. */
    function isTruncated(title) {
        return title.scrollHeight > title.clientHeight + 1;
    }

    function ensureTooltip(results) {
        if (tooltip && tooltip.parentElement === results) return tooltip;

        tooltip = document.createElement('div');
        tooltip.className = 'snize-title-tooltip';
        /* Decorative. The full name is already the link's own text -- only the
           display of it is clipped -- so a screen reader reads it either way. */
        tooltip.setAttribute('aria-hidden', 'true');
        results.appendChild(tooltip);

        /* Deferred until there is something to keep in place: a phone never
           reaches here, so it never carries these listeners. The panel scrolls
           on a short window and position:fixed does not follow it -- capture,
           so the panel's own scroll is heard and not just the page's. */
        if (!tooltipTracked) {
            tooltipTracked = true;
            window.addEventListener('scroll', positionTooltip, {
                capture: true,
                passive: true,
            });
            window.addEventListener('resize', positionTooltip);
        }

        return tooltip;
    }

    function hideTooltip() {
        tooltipTitle = null;
        if (tooltip) tooltip.classList.remove('is-visible');
    }

    function positionTooltip() {
        if (!tooltip || !tooltipTitle || !tooltipTitle.isConnected) {
            return hideTooltip();
        }

        var results = document.getElementById(RESULTS_ID);
        if (!results) return hideTooltip();

        var title = tooltipTitle.getBoundingClientRect();
        var panel = results.getBoundingClientRect();
        var width = tooltip.offsetWidth;

        /* Centre on the card, then pull the box back inside the panel where
           that would hang it over an edge -- the outer columns sit less than
           half a box away from one. */
        var centre = title.left + title.width / 2;
        var left = centre - width / 2;
        var min = panel.left + TOOLTIP_EDGE;
        var max = panel.right - TOOLTIP_EDGE - width;
        if (max > min) left = Math.min(Math.max(left, min), max);

        var style = tooltip.style;
        style.setProperty('--tt-left', Math.round(left) + 'px');
        style.setProperty('--tt-top', Math.round(title.bottom) + 'px');
        /* So the caret still points at the card after that nudge. */
        style.setProperty('--tt-arrow-x', Math.round(centre - left) + 'px');
    }

    function showTooltip(title) {
        var results = document.getElementById(RESULTS_ID);
        if (!results) return;

        var box = ensureTooltip(results);
        box.dataset.title = title.textContent.trim();
        tooltipTitle = title;
        positionTooltip();
        box.classList.add('is-visible');
    }

    function watchTitleHover() {
        /* Delegated from the document so it survives the widget rebuilding its
           product list, which it does on every keystroke. mouseover/mouseout
           rather than mouseenter/mouseleave, because only those bubble. */
        document.addEventListener('mouseover', function (event) {
            if (!hoverCapable.matches) return;

            /* This runs on every mouse move over the page, so the class test
               comes first and the ancestor walk only happens once it passes.
               .snize-title holds nothing but text, so when the pointer is over
               one it is the target rather than an ancestor of it. */
            var target = event.target;
            var title =
                target &&
                target.classList &&
                target.classList.contains('snize-title') &&
                target.closest('#' + RESULTS_ID + ' .snize-product')
                    ? target
                    : null;

            /* Anything else in the panel puts the box away again -- "Hovering
               on the image will not trigger this." */
            if (!title) {
                if (tooltipTitle) hideTooltip();
                return;
            }

            if (title === tooltipTitle) return;
            if (!isTruncated(title)) return hideTooltip();

            showTooltip(title);
        });

        /* Covers the pointer leaving the window, which fires no mouseover. */
        document.addEventListener('mouseout', function (event) {
            if (tooltipTitle && event.target === tooltipTitle) hideTooltip();
        });
    }

    /* ----------------------------------------------------------------------
       Wiring
       ---------------------------------------------------------------------- */

    function initAll() {
        document.querySelectorAll('.header-search').forEach(initSearchBar);
        syncIdleClass();
    }

    function watchDropdown() {
        var observed = null;
        var queued = false;

        /* Coalesce the burst of mutations the widget emits per keystroke.
           setTimeout rather than requestAnimationFrame: rAF does not fire at
           all while the tab is hidden, which leaves `queued` stuck true and
           silently drops every later pass. This is DOM reordering, not
           animation, so it does not need to be frame-aligned. */
        function scheduleLayout() {
            if (queued) return;
            queued = true;
            window.setTimeout(function () {
                queued = false;
                layoutDropdown();
            }, 0);
        }

        function attach(node) {
            if (observed === node) return;
            observed = node;
            new MutationObserver(scheduleLayout).observe(node, {
                childList: true,
                subtree: true,
            });
            scheduleLayout();
        }

        var existing = document.getElementById(RESULTS_ID);
        if (existing) attach(existing);

        // The widget is injected on first interaction, so watch for it.
        new MutationObserver(function () {
            var node = document.getElementById(RESULTS_ID);
            if (node) attach(node);
            suppressAppVoiceTrigger();
        }).observe(document.body, { childList: true });

        window.addEventListener('resize', layoutDropdown);
    }

    function boot() {
        initAll();
        watchDropdown();
        watchTitleHover();
        stripUnknownResultsTab();

        // Header blocks can be re-rendered by the theme editor.
        document.addEventListener('shopify:section:load', initAll);

        /* Searchanise fires Searchanise.AutocompleteUpdated through jQuery.
           jQuery custom events are not dispatched as native DOM events, so a
           plain addEventListener never hears it -- bind through jQuery when it
           is present. The MutationObserver above is the real safety net; this
           is belt and braces for updates that somehow do not mutate the DOM. */
        if (window.jQuery) {
            window
                .jQuery(document)
                .on('Searchanise.AutocompleteUpdated', layoutDropdown);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
